import React, { useState, useEffect, useRef } from 'react';
import { Button } from './Button';
import { useInventory } from '../../contexts/InventoryContext';
import { useAuth } from '../../contexts/AuthContext';
import { HapticFeedback } from './HapticFeedback';
import { InventoryItem } from '../../types';
import { getAdminId } from '../../utils/getAdminId';
import { EPC_HEX_LENGTH, classifyScanValue } from '../../utils/scanClassify';
import { resolveEpcScan } from '../../lib/services/rfidScanResolveService';
import {
  X,
  CheckCircle,
  Plus,
  Zap,
  Target,
  AlertCircle,
  AlertTriangle
} from 'lucide-react';

interface ExternalScannerProps {
  onScan: (result: string, item?: InventoryItem) => void;
  onClose: () => void;
  isOpen: boolean;
  mode: 'search' | 'incoming' | 'outgoing' | 'payment';
  title?: string;
}

type HistoryAction = 'found' | 'created' | 'error' | 'uhf' | 'unknown' | 'integrity';

interface ScanAlert {
  tone: 'amber' | 'red';
  title: string;
  detail: string;
  epc?: string;
  decoded?: string;
}

export const ExternalScanner: React.FC<ExternalScannerProps> = ({
  onScan,
  onClose,
  isOpen,
  mode,
  title = 'External Scanner'
}) => {
  const { user } = useAuth();
  const adminId = getAdminId(user);
  const { items, addItem } = useInventory();
  const [scanBuffer, setScanBuffer] = useState('');
  const [scanResults, setScanResults] = useState<string[]>([]);
  const [continuousMode, setContinuousMode] = useState(true);
  const [autoCreateItems, setAutoCreateItems] = useState(mode === 'incoming');
  const [scanAlert, setScanAlert] = useState<ScanAlert | null>(null);
  const [scanHistory, setScanHistory] = useState<Array<{
    barcode: string;
    timestamp: Date;
    action: HistoryAction;
    itemName?: string;
  }>>([]);

  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScanRef = useRef<string>('');
  const lastScanTimeRef = useRef<number>(0);
  const processingRef = useRef<boolean>(false);
  const bufferRef = useRef<string>('');
  const handleScanResultRef = useRef<(barcode: string) => Promise<void>>();
  const handleEpcScanRef = useRef<(epc: string) => Promise<void>>();
  const handleUnknownScanRef = useRef<(raw: string) => void>();

  const handleScanResult = async (barcode: string) => {
    HapticFeedback.scan();

    setScanResults(prev => [barcode, ...prev.slice(0, 9)]);

    const existingItem = items.find(item =>
      item.batch === barcode ||
      item.barcode === barcode ||
      item.sku === barcode ||
      item.name.toLowerCase().includes(barcode.toLowerCase()) ||
      item.id === barcode
    );

    if (existingItem) {
      setScanHistory(prev => [{
        barcode,
        timestamp: new Date(),
        action: 'found',
        itemName: existingItem.name
      }, ...prev.slice(0, 19)]);

      HapticFeedback.success();
      onScan(barcode, existingItem);

      if (!continuousMode || mode === 'search') {
        setTimeout(() => onClose(), 500);
      }
    } else if (autoCreateItems && mode === 'incoming') {
      try {
        const newItem = await addItem({
          name: `Scanned Item ${barcode}`,
          category: 'General',
          quantity: 0,
          batch: barcode,
          barcode: barcode,
        });

        if (newItem) {
          setScanHistory(prev => [{
            barcode,
            timestamp: new Date(),
            action: 'created',
            itemName: newItem.name
          }, ...prev.slice(0, 19)]);

          HapticFeedback.success();
          onScan(barcode, newItem);
        }
      } catch (error) {
        setScanHistory(prev => [{
          barcode,
          timestamp: new Date(),
          action: 'error'
        }, ...prev.slice(0, 19)]);

        HapticFeedback.error();
      }
    } else {
      setScanHistory(prev => [{
        barcode,
        timestamp: new Date(),
        action: 'error'
      }, ...prev.slice(0, 19)]);

      HapticFeedback.light();
      onScan(barcode);

      if (!continuousMode || mode === 'search') {
        setTimeout(() => onClose(), 500);
      }
    }
  };

  const handleEpcScan = async (epc: string) => {
    HapticFeedback.scan();
    setScanResults(prev => [epc, ...prev.slice(0, 9)]);

    if (!adminId) {
      setScanAlert({
        tone: 'red',
        title: 'UHF scan blocked',
        detail: 'No active company context to resolve this EPC.',
        epc,
      });
      HapticFeedback.error();
      setScanHistory(prev => [{
        barcode: epc,
        timestamp: new Date(),
        action: 'error',
      }, ...prev.slice(0, 19)]);
      return;
    }

    try {
      const result = await resolveEpcScan(adminId, epc);
      const decodedSummary = [
        result.decoded.styleCode && `style ${result.decoded.styleCode}`,
        result.decoded.itemCode && `item ${result.decoded.itemCode}`,
        result.decoded.colorCode && `color ${result.decoded.colorCode}`,
        result.decoded.sizeCode && `size ${result.decoded.sizeCode}`,
        `serial ${result.serial}`,
      ].filter(Boolean).join(' · ');

      if (!result.success || !result.item) {
        setScanAlert({
          tone: 'red',
          title: 'UHF EPC could not be resolved',
          detail: result.message || 'Unknown resolve failure',
          epc: result.epcFull,
          decoded: decodedSummary,
        });
        HapticFeedback.error();
        setScanHistory(prev => [{
          barcode: epc,
          timestamp: new Date(),
          action: 'error',
        }, ...prev.slice(0, 19)]);
        return;
      }

      if (result.integrityAlarm) {
        setScanAlert({
          tone: 'red',
          title: 'DATA INTEGRITY ALARM',
          detail: result.message || 'Unit record and EPC disagree',
          epc: result.epcFull,
          decoded: decodedSummary,
        });
        HapticFeedback.error();
        setScanHistory(prev => [{
          barcode: epc,
          timestamp: new Date(),
          action: 'integrity',
          itemName: result.item?.name,
        }, ...prev.slice(0, 19)]);
      } else if (!result.unitFound) {
        setScanAlert({
          tone: 'amber',
          title: 'No unit record for this EPC',
          detail: result.message || 'Variant resolved from EPC base only.',
          epc: result.epcFull,
          decoded: decodedSummary,
        });
        HapticFeedback.light();
        setScanHistory(prev => [{
          barcode: epc,
          timestamp: new Date(),
          action: 'uhf',
          itemName: result.item.name,
        }, ...prev.slice(0, 19)]);
      } else {
        setScanAlert(null);
        HapticFeedback.success();
        setScanHistory(prev => [{
          barcode: epc,
          timestamp: new Date(),
          action: 'uhf',
          itemName: result.item.name,
        }, ...prev.slice(0, 19)]);
      }

      const scanValue = result.item.barcode || result.item.sku || result.epcBase;
      onScan(scanValue, result.item);

      if (!continuousMode || mode === 'search') {
        setTimeout(() => onClose(), 500);
      }
    } catch (err) {
      setScanAlert({
        tone: 'red',
        title: 'UHF resolve failed',
        detail: err instanceof Error ? err.message : 'Unexpected error',
        epc,
      });
      HapticFeedback.error();
      setScanHistory(prev => [{
        barcode: epc,
        timestamp: new Date(),
        action: 'error',
      }, ...prev.slice(0, 19)]);
    }
  };

  const handleUnknownScan = (raw: string) => {
    HapticFeedback.error();
    setScanResults(prev => [raw, ...prev.slice(0, 9)]);
    setScanAlert({
      tone: 'amber',
      title: 'Unknown scan value',
      detail: `Not a ${EPC_HEX_LENGTH}-character hex EPC and not an 8–14 digit barcode. Value was not discarded.`,
      epc: raw,
    });
    setScanHistory(prev => [{
      barcode: raw,
      timestamp: new Date(),
      action: 'unknown',
    }, ...prev.slice(0, 19)]);
  };

  handleScanResultRef.current = handleScanResult;
  handleEpcScanRef.current = handleEpcScan;
  handleUnknownScanRef.current = handleUnknownScan;

  useEffect(() => {
    if (isOpen) {
      const handleKeyPress = (e: KeyboardEvent) => {
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
        }

        const now = Date.now();

        if (e.key === 'Enter') {
          e.preventDefault();
          const currentBuffer = bufferRef.current;

          if (currentBuffer.length > 0 && !processingRef.current) {
            const kind = classifyScanValue(currentBuffer);
            const dedupeMs = kind === 'epc' ? 1200 : 150;
            const timeSinceLastScan = now - lastScanTimeRef.current;

            if (timeSinceLastScan < dedupeMs && currentBuffer === lastScanRef.current) {
              bufferRef.current = '';
              setScanBuffer('');
              return;
            }

            processingRef.current = true;
            lastScanRef.current = currentBuffer;
            lastScanTimeRef.current = now;

            if (kind === 'barcode') {
              void handleScanResultRef.current?.(currentBuffer);
            } else if (kind === 'epc') {
              void handleEpcScanRef.current?.(currentBuffer);
            } else {
              handleUnknownScanRef.current?.(currentBuffer);
            }

            bufferRef.current = '';
            setScanBuffer('');

            setTimeout(() => {
              processingRef.current = false;
            }, 200);
          } else {
            bufferRef.current = '';
            setScanBuffer('');
          }
        } else if (e.key.length === 1) {
          const timeSinceLastScan = now - lastScanTimeRef.current;

          if (timeSinceLastScan < 100 && bufferRef.current.length === 0) {
            return;
          }

          bufferRef.current += e.key;
          setScanBuffer(bufferRef.current);

          scanTimeoutRef.current = setTimeout(() => {
            bufferRef.current = '';
            setScanBuffer('');
          }, 300);
        }
      };

      window.addEventListener('keypress', handleKeyPress);
      return () => {
        window.removeEventListener('keypress', handleKeyPress);
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
        }
        bufferRef.current = '';
        processingRef.current = false;
      };
    }
  }, [isOpen]);

  const clearHistory = () => {
    setScanResults([]);
    setScanHistory([]);
    setScanAlert(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-6 border max-w-5xl shadow-xl rounded-xl bg-white dark:bg-gray-800">
        <div className="mt-2">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-medium text-gray-900 dark:text-gray-100 flex items-center">
              <Zap className="h-7 w-7 mr-3 text-green-500" />
              {title}
              <span className="ml-2 text-sm bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded">
                EXTERNAL DEVICE
              </span>
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-7 w-7" />
            </button>
          </div>

          {scanAlert && (
            <div
              className={`mb-4 rounded-lg border px-4 py-3 ${
                scanAlert.tone === 'red'
                  ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 text-red-900 dark:text-red-100'
                  : 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100'
              }`}
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold">{scanAlert.title}</div>
                  <div className="text-sm mt-1">{scanAlert.detail}</div>
                  {scanAlert.epc && (
                    <div className="text-xs font-mono mt-2 break-all opacity-90">{scanAlert.epc}</div>
                  )}
                  {scanAlert.decoded && (
                    <div className="text-xs mt-1 opacity-90">{scanAlert.decoded}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="card p-8 text-center">
                <Zap className="h-24 w-24 mx-auto mb-4 text-green-500 animate-pulse" />
                <h4 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Ready to Scan
                </h4>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Use your external barcode or UHF scanner. Values are classified automatically.
                </p>

                {scanBuffer && (
                  <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Reading...</p>
                    <p className="font-mono text-lg text-blue-600 dark:text-blue-400">{scanBuffer}</p>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 justify-center mb-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="continuous"
                      checked={continuousMode}
                      onChange={(e) => setContinuousMode(e.target.checked)}
                      className="mr-2"
                    />
                    <label htmlFor="continuous" className="text-sm text-gray-700 dark:text-gray-300">
                      Continuous Scanning
                    </label>
                  </div>

                  {mode === 'incoming' && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="autoCreate"
                        checked={autoCreateItems}
                        onChange={(e) => setAutoCreateItems(e.target.checked)}
                        className="mr-2"
                      />
                      <label htmlFor="autoCreate" className="text-sm text-gray-700 dark:text-gray-300">
                        Auto-create Items
                      </label>
                    </div>
                  )}
                </div>

                <Button onClick={clearHistory} variant="secondary">
                  Clear History
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="card p-4">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                  <Target className="h-4 w-4 mr-2" />
                  Recent Scans
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {scanResults.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No scans yet</p>
                  ) : (
                    scanResults.map((result, index) => (
                      <div key={index} className="text-xs font-mono bg-gray-100 dark:bg-gray-700 p-2 rounded break-all">
                        {result}
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="card p-4">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                  Scan History
                </h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {scanHistory.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No history yet</p>
                  ) : (
                    scanHistory.map((scan, index) => (
                      <div key={index} className="text-xs p-2 rounded border-l-2 border-gray-200 dark:border-gray-600">
                        <div className={`flex items-center text-xs font-medium ${
                          scan.action === 'found' || scan.action === 'uhf' ? 'text-green-600 dark:text-green-400' :
                          scan.action === 'created' ? 'text-blue-600 dark:text-blue-400' :
                          scan.action === 'integrity' ? 'text-red-700 dark:text-red-300' :
                          scan.action === 'unknown' ? 'text-amber-600 dark:text-amber-400' :
                          'text-red-600 dark:text-red-400'
                        }`}>
                          {scan.action === 'found' || scan.action === 'uhf' ? (
                            <CheckCircle className="h-3 w-3 mr-1" />
                          ) : scan.action === 'created' ? (
                            <Plus className="h-3 w-3 mr-1" />
                          ) : (
                            <AlertCircle className="h-3 w-3 mr-1" />
                          )}
                          {scan.action === 'found' ? 'Found' :
                           scan.action === 'created' ? 'Created' :
                           scan.action === 'uhf' ? 'UHF Resolved' :
                           scan.action === 'integrity' ? 'Integrity Alarm' :
                           scan.action === 'unknown' ? 'Unknown' : 'Not Found'}
                        </div>
                        <div className="text-gray-600 dark:text-gray-400 font-mono break-all">
                          {scan.barcode}
                        </div>
                        {scan.itemName && (
                          <div className="text-gray-700 dark:text-gray-300 truncate">
                            {scan.itemName}
                          </div>
                        )}
                        <div className="text-gray-500 dark:text-gray-400">
                          {scan.timestamp.toLocaleTimeString()}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="card p-4">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Scanner Mode
                </h4>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span>Mode:</span>
                    <span className="font-medium capitalize">{mode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Auto-create:</span>
                    <span>{autoCreateItems ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Continuous:</span>
                    <span>{continuousMode ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <h4 className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">
              External Scanner Instructions:
            </h4>
            <ul className="text-sm text-green-600 dark:text-green-400 space-y-1">
              <li>• <strong>Compatible Devices:</strong> USB/Bluetooth keyboard-wedge barcode and UHF (P80 tablet)</li>
              <li>• <strong>Automatic Classification:</strong> 32-hex = UHF EPC; 8–14 digits = barcode; otherwise flagged</li>
              <li>• <strong>Smart Matching:</strong> Barcodes match inventory; EPCs decode via active RFID scheme</li>
              <li>• <strong>Auto-creation:</strong> {autoCreateItems ? 'Creates new items from unknown barcodes' : 'Disabled for this mode'}</li>
              <li>• <strong>Continuous Mode:</strong> {continuousMode ? 'Keeps scanning multiple items' : 'Single scan mode'}</li>
              <li>• <strong>No Camera Required:</strong> Works with physical scanner devices only</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
